import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ShipmentStatusService } from 'app/modules/admin/supply-chain/services/shipment-status.service';
import { SharedModule } from 'app/modules/shared/shared.module';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { GlobalSearchPage, GlobalSearchResult, GlobalSearchService } from './global-search.service';

/** Below this the search is useless (it would match half the archive) and the BE returns nothing */
const MIN_TERM_LENGTH = 2;
const PAGE_SIZE = 10;
const DEBOUNCE_MS = 300;
/** Distance from the bottom of the panel at which the next page is requested */
const SCROLL_THRESHOLD_PX = 48;

const EMPTY_PAGE: GlobalSearchPage = { items: [], hasMore: false, totalCount: 0 };

@Component({
    selector: 'app-global-search',
    templateUrl: './global-search.component.html',
    imports: [CommonModule, SharedModule],
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
    @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

    searchControl = new FormControl<string>('', { nonNullable: true });
    /** Pending confirmations first: they are few and need an action, the POs follow */
    pendingResults: GlobalSearchResult[] = [];
    purchaseOrderResults: GlobalSearchResult[] = [];
    totalCount = 0;
    loading = false;
    loadingMore = false;
    panelOpen = false;
    /** Row highlighted by the arrow keys, -1 when the user has not moved yet */
    activeIndex = -1;

    /** Term the results on screen belong to: without it a term never searched would read as "no results" */
    private lastSearchedTerm = '';

    private pendingHasMore = false;
    private purchaseOrdersHasMore = false;
    /** Kept per source: a page not requested any more must not zero the counter */
    private pendingTotal = 0;
    private purchaseOrdersTotal = 0;
    private _unsubscribeAll = new Subject<void>();

    constructor(
        private globalSearchService: GlobalSearchService,
        private shipmentStatusService: ShipmentStatusService,
        private router: Router,
        private elementRef: ElementRef<HTMLElement>
    ) {}

    get canSearch(): boolean {
        return this.globalSearchService.canSearch;
    }

    get term(): string {
        return this.searchControl.value.trim();
    }

    get results(): GlobalSearchResult[] {
        return [...this.pendingResults, ...this.purchaseOrderResults];
    }

    get hasMore(): boolean {
        return this.pendingHasMore || this.purchaseOrdersHasMore;
    }

    get showNoResults(): boolean {
        return (
            this.panelOpen &&
            !this.loading &&
            this.results.length === 0 &&
            this.term.length >= MIN_TERM_LENGTH &&
            this.lastSearchedTerm === this.term
        );
    }

    ngOnInit(): void {
        this.searchControl.valueChanges
            .pipe(
                debounceTime(DEBOUNCE_MS),
                distinctUntilChanged(),
                tap((value) => {
                    const term = value.trim();
                    this.loading = term.length >= MIN_TERM_LENGTH;
                    this.panelOpen = this.loading;
                    this.reset();
                }),
                switchMap((value) => {
                    const term = value.trim();
                    if (term.length < MIN_TERM_LENGTH) {
                        return of([EMPTY_PAGE, EMPTY_PAGE]);
                    }
                    return this.loadPage(0);
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(([pendingPage, purchaseOrderPage]) => {
                this.append(pendingPage, purchaseOrderPage);
                this.lastSearchedTerm = this.term;
                this.loading = false;
                this.panelOpen = this.term.length >= MIN_TERM_LENGTH;
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /** A click anywhere else closes the panel: it floats over the page content */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target as Node)) {
            this.panelOpen = false;
        }
    }

    onFocus(): void {
        if (this.results.length > 0 || (this.term.length >= MIN_TERM_LENGTH && this.lastSearchedTerm === this.term)) {
            this.panelOpen = true;
        }
    }

    /** Infinite scroll: the next page is fetched before the list actually ends */
    onPanelScroll(event: Event): void {
        const panel = event.target as HTMLElement;
        const reachedBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - SCROLL_THRESHOLD_PX;

        if (!reachedBottom || this.loading || this.loadingMore || !this.hasMore) {
            return;
        }

        // The term the page was asked for: a load-more in flight when the user keeps typing must
        // not append rows of the previous search (and corrupt the skip of the next page)
        const requestTerm = this.term;
        this.loadingMore = true;
        this.loadPage(null)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(([pendingPage, purchaseOrderPage]) => {
                if (requestTerm !== this.term) {
                    return;
                }
                this.append(pendingPage, purchaseOrderPage);
                this.loadingMore = false;
            });
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.close();
            return;
        }
        const results = this.results;
        if (!this.panelOpen || results.length === 0) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.activeIndex = (this.activeIndex + 1) % results.length;
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.activeIndex = this.activeIndex <= 0 ? results.length - 1 : this.activeIndex - 1;
        } else if (event.key === 'Enter') {
            event.preventDefault();
            this.openResult(results[this.activeIndex >= 0 ? this.activeIndex : 0]);
        }
    }

    openResult(result: GlobalSearchResult | undefined): void {
        if (!result?.id) {
            return;
        }

        if (result.kind === 'pendingConfirmation') {
            // Not a PO yet: it exists only as a card on the dashboard, which opens it from this param
            this.router.navigate(['/supply-chain/dashboard'], { queryParams: { pendingConfirmationId: result.id } });
        } else {
            this.router.navigate(['/supply-chain/tracking', result.id]);
        }

        // Term and results are kept: reopening the search goes back to the list just used,
        // the X is the only thing that clears it
        this.close();
    }

    clear(): void {
        this.searchControl.setValue('');
        this.reset();
        this.panelOpen = false;
        this.searchInput?.nativeElement.focus();
    }

    close(): void {
        this.panelOpen = false;
        this.activeIndex = -1;
    }

    getStatusBadgeStyle(statusId: string | null | undefined): Record<string, string> {
        return this.shipmentStatusService.getStatusBadgeStyle(statusId, null);
    }

    /** The subtitle of a row: only the fields the PO actually has, joined by a dot */
    getResultDetails(result: GlobalSearchResult): string {
        return [result.vendorName, result.warehouseName, result.vesselName].filter((value) => !!value).join(' · ');
    }

    trackByResult(_index: number, result: GlobalSearchResult): string {
        return `${result.kind}-${result.id}`;
    }

    /**
     * Requests one page from each source. `skip: null` means "continue from what is already
     * loaded"; a source with nothing left to give is not asked again.
     */
    private loadPage(skip: number | null) {
        const term = this.term;
        const first = skip === 0;

        const pending$ =
            first || this.pendingHasMore
                ? this.globalSearchService
                      .searchPendingConfirmations(term, first ? 0 : this.pendingResults.length, PAGE_SIZE)
                      .pipe(catchError(() => of({ ...EMPTY_PAGE, totalCount: this.pendingTotal })))
                : of({ ...EMPTY_PAGE, totalCount: this.pendingTotal });

        const purchaseOrders$ =
            first || this.purchaseOrdersHasMore
                ? this.globalSearchService
                      .searchPurchaseOrders(term, first ? 0 : this.purchaseOrderResults.length, PAGE_SIZE)
                      .pipe(catchError(() => of({ ...EMPTY_PAGE, totalCount: this.purchaseOrdersTotal })))
                : of({ ...EMPTY_PAGE, totalCount: this.purchaseOrdersTotal });

        return forkJoin([pending$, purchaseOrders$]);
    }

    private append(pendingPage: GlobalSearchPage, purchaseOrderPage: GlobalSearchPage): void {
        this.pendingResults = [...this.pendingResults, ...pendingPage.items];
        this.purchaseOrderResults = [...this.purchaseOrderResults, ...purchaseOrderPage.items];
        this.pendingHasMore = pendingPage.hasMore;
        this.purchaseOrdersHasMore = purchaseOrderPage.hasMore;
        this.pendingTotal = pendingPage.totalCount;
        this.purchaseOrdersTotal = purchaseOrderPage.totalCount;
        this.totalCount = this.pendingTotal + this.purchaseOrdersTotal;
    }

    private reset(): void {
        this.pendingResults = [];
        this.purchaseOrderResults = [];
        this.pendingHasMore = false;
        this.purchaseOrdersHasMore = false;
        this.pendingTotal = 0;
        this.purchaseOrdersTotal = 0;
        this.totalCount = 0;
        this.activeIndex = -1;
        this.loadingMore = false;
        this.lastSearchedTerm = '';
    }
}
